import { AxlePage } from '@/components/layout/AxlePage';
import { PageTitle } from '@/components/typography';
import { DARK_NAV_TEAL, TEXT_GRAY } from '@/constants/colors';
import { AxleV2Theme } from '@/theme';
import { Children, Component } from '@/utils/reactTypes';
import { Box, SxProps, Theme, ThemeProvider } from '@mui/material';

// Grid areas are assigned by class name, except `.navigation`, which AllVisitsTabBar sets itself.
const allVisitsSx: SxProps<Theme> = {
    height: '100%',
    display: 'grid',
    // A single shrinkable column, so the grid never grows past the space beside the map.
    gridTemplateColumns: 'minmax(0, 1fr)',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `
        "heading"
        "navigation"
        "table"
        "footer"
    `,
    // Grid items default to their content's width as a minimum; let them shrink instead.
    '& > *': { minWidth: 0 },
    // The title and filters share a wrapping row: the filters drop below the title when both
    //  don't fit, then wrap among themselves.
    '& .heading': {
        gridArea: 'heading',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '0.5rem',
    },
    '& .filters': {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
        minWidth: 0,
    },
    '& .table': {
        gridArea: 'table',
        borderWidth: '0 1px 1px 1px',
        borderStyle: 'solid',
        borderColor: DARK_NAV_TEAL,
        borderRadius: '0 0 10px 10px',
    },
    '& .table .empty-field': {
        fontStyle: 'italic',
        color: TEXT_GRAY,
        fontSize: '12px',
    },
    '& .footer': { gridArea: 'footer' },
};

type AllVisitsFrameProps = {
    filters?: Children;
    tabs?: Children;
    table?: Children;
    map?: Component;
};

// The page's layout and slots, shared with its route fallback so both keep the same shape.
export const AllVisitsFrame = ({ filters, tabs, table, map }: AllVisitsFrameProps) => (
    <ThemeProvider theme={AxleV2Theme}>
        <AxlePage
            pageTitle='All Visits'
            map={map}
            mapSize={{
                mapBreakpoint: 'md',
                mapHorizontalPercentageDefault: 20,
                mapVerticalPercentageDefault: 15,
            }}
        >
            <Box className='all-visits' sx={allVisitsSx}>
                <Box className='heading'>
                    <PageTitle className='page-header'>All Visits</PageTitle>
                    {filters}
                </Box>
                {tabs}
                {table}
            </Box>
        </AxlePage>
    </ThemeProvider>
);
